import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Save, Trash2, Copy, Trophy } from "lucide-react";
import { compressImage } from "@/lib/imageCompression";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";
import { catchSchema } from "@/lib/validations";
import exifr from "exifr";
import { FishSpeciesCombobox } from "@/components/catches/FishSpeciesCombobox";

interface CatchEntry {
  id: string;
  fish_species: string;
  date_caught: string;
  weight_lbs: string;
  length_inches: string;
  girth_inches: string;
  general_location: string;
  boat_name: string;
  captain_name: string;
  line_test: string;
  video_url: string;
  latitude: number | null;
  longitude: number | null;
  useTunaCalculation: boolean;
  photo_file: File | null;
}

export default function BulkAddCatch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState<CatchEntry[]>([
    createEmptyEntry(),
  ]);

  function createEmptyEntry(): CatchEntry {
    return {
      id: Date.now().toString() + Math.random(),
      fish_species: "",
      date_caught: new Date().toISOString().split("T")[0],
      weight_lbs: "",
      length_inches: "",
      girth_inches: "",
      general_location: "",
      boat_name: "",
      captain_name: "",
      line_test: "",
      video_url: "",
      latitude: null,
      longitude: null,
      useTunaCalculation: false,
      photo_file: null,
    };
  }

  const duplicateEntry = (id: string) => {
    const entryToDuplicate = entries.find((entry) => entry.id === id);
    if (entryToDuplicate) {
      const duplicated: CatchEntry = {
        ...entryToDuplicate,
        id: Date.now().toString() + Math.random(),
        weight_lbs: "",
        length_inches: "",
        girth_inches: "",
        photo_file: null,
      };
      setEntries([...entries, duplicated]);
      toast.success("Catch duplicated without measurements and photo");
    }
  };

  const addEntry = () => {
    setEntries([...entries, createEmptyEntry()]);
  };

  const removeEntry = (id: string) => {
    if (entries.length === 1) {
      toast.error("You must have at least one catch entry");
      return;
    }
    setEntries(entries.filter((entry) => entry.id !== id));
  };

  const updateEntry = (id: string, field: keyof CatchEntry, value: any) => {
    setEntries(
      entries.map((entry) => {
        if (entry.id !== id) return entry;

        const updated = { ...entry, [field]: value };

        // Auto-calculate weight for tuna if checkbox is enabled
        if (
          (field === "girth_inches" || field === "length_inches" || field === "useTunaCalculation") &&
          updated.useTunaCalculation &&
          updated.fish_species.toLowerCase().includes("tuna") &&
          updated.girth_inches &&
          updated.length_inches
        ) {
          const girth = parseFloat(updated.girth_inches);
          const length = parseFloat(updated.length_inches);
          if (!isNaN(girth) && !isNaN(length) && girth > 0 && length > 0) {
            updated.weight_lbs = (((girth * girth) * length) / 800).toFixed(2);
          }
        }

        return updated;
      })
    );
  };

  const reverseGeocode = async (lat: number, lon: number): Promise<string | null> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=10`,
        {
          headers: {
            'User-Agent': 'FishingWeightsApp/1.0'
          }
        }
      );
      
      if (!response.ok) return null;
      
      const data = await response.json();
      
      // Prioritize useful location info: city, harbor, bay, ocean area
      const address = data.address || {};
      const locationName = 
        address.harbour || 
        address.bay || 
        address.city || 
        address.town || 
        address.village || 
        address.state || 
        data.display_name?.split(',').slice(0, 2).join(', ');
      
      return locationName || null;
    } catch (error) {
      console.error("Reverse geocoding failed:", error);
      return null;
    }
  };

  const handlePhotoChange = (entryId: string, file: File | null) => {
    // Immediately set the photo file - don't wait for EXIF
    updateEntry(entryId, "photo_file", file);
    
    if (!file) return;

    // Extract EXIF data in background - don't block photo upload
    exifr.parse(file, {
      gps: true,
      pick: ['DateTimeOriginal', 'CreateDate', 'latitude', 'longitude']
    })
      .then(async (exifData) => {
        console.log("EXIF data extracted:", exifData);
        
        if (!exifData) {
          console.log("No EXIF data in photo");
          toast.info("No GPS data found in photo", {
            description: "You can manually enter the location"
          });
          return;
        }

        // Extract GPS coordinates
        if (exifData.latitude && exifData.longitude) {
          console.log("GPS coordinates found:", exifData.latitude, exifData.longitude);
          updateEntry(entryId, "latitude", exifData.latitude);
          updateEntry(entryId, "longitude", exifData.longitude);
          
          // Reverse geocode to get location name
          try {
            const locationName = await reverseGeocode(exifData.latitude, exifData.longitude);
            
            if (locationName) {
              console.log("Location name found:", locationName);
              updateEntry(entryId, "general_location", locationName);
              toast.success(`Location Found: ${locationName}`, {
                description: "GPS coordinates extracted from photo"
              });
            } else {
              console.log("Reverse geocoding returned no location name");
              toast.success("GPS coordinates extracted from photo");
            }
          } catch (error) {
            console.error("Reverse geocoding failed:", error);
            toast.warning("GPS found but couldn't determine location name", {
              description: "You can manually enter the location"
            });
          }
        } else {
          console.log("Photo has EXIF but no GPS coordinates");
          toast.info("No GPS data found in photo", {
            description: "You can manually enter the location"
          });
        }

        // Extract date
        const photoDate = exifData.DateTimeOriginal || exifData.CreateDate;
        if (photoDate) {
          try {
            const dateString = new Date(photoDate).toISOString().split("T")[0];
            updateEntry(entryId, "date_caught", dateString);
            toast.success("Date extracted from photo!");
          } catch (error) {
            console.error("Failed to parse photo date:", error);
          }
        }
      })
      .catch((error) => {
        console.error("EXIF parsing failed:", error);
        toast.info("Couldn't read photo metadata", {
          description: "You can manually enter the location and date"
        });
      });
  };

  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate all entries
      for (const entry of entries) {
        const validationData = {
          fish_species: entry.fish_species,
          date_caught: entry.date_caught,
          weight_lbs: entry.weight_lbs,
          length_inches: entry.length_inches || undefined,
          girth_inches: entry.girth_inches || undefined,
          general_location: entry.general_location,
          boat_name: entry.boat_name || undefined,
          captain_name: entry.captain_name || undefined,
          line_test: entry.line_test || undefined,
        };

        const result = catchSchema.safeParse(validationData);
        if (!result.success) {
          const firstError = result.error.errors[0];
          toast.error(`Validation error: ${firstError.message}`, {
            description: `Issue with ${firstError.path.join('.')}`,
          });
          setLoading(false);
          return;
        }
      }

      // Upload photos first and get URLs
      const catchesWithPhotos = await Promise.all(
        entries.map(async (entry) => {
          let photo_url = null;

          if (entry.photo_file) {
            const compressedFile = await compressImage(entry.photo_file);
            const fileExt = compressedFile.name.split(".").pop();
            const fileName = `${user?.id}/${Date.now()}_${Math.random()}.${fileExt}`;

            const { error: uploadError } = await supabase.storage
              .from("catch-photos")
              .upload(fileName, compressedFile);

            if (uploadError) throw uploadError;

            const { data: urlData } = supabase.storage
              .from("catch-photos")
              .getPublicUrl(fileName);

            photo_url = urlData.publicUrl;
          }

          return {
            angler_id: user?.id,
            fish_species: entry.fish_species,
            date_caught: entry.date_caught,
            weight_lbs: parseFloat(entry.weight_lbs),
            length_inches: entry.length_inches ? parseFloat(entry.length_inches) : null,
            girth_inches: entry.girth_inches ? parseFloat(entry.girth_inches) : null,
            general_location: entry.general_location,
            boat_name: entry.boat_name || null,
            captain_name: entry.captain_name || null,
            line_test: entry.line_test || null,
            video_url: entry.video_url || null,
            latitude: entry.latitude,
            longitude: entry.longitude,
            photo_url,
          };
        })
      );

      const { error } = await supabase.from("catches").insert(catchesWithPhotos);

      if (error) throw error;

      // Track analytics event
      trackEvent({ 
        eventName: AnalyticsEvents.BULK_UPLOAD, 
        properties: { count: entries.length } 
      });

      // Show success animation
      setShowSuccess(true);
      await new Promise(resolve => setTimeout(resolve, 1500));

      toast.success(`${entries.length} catches recorded successfully!`);
      navigate("/dashboard");
    } catch (error: any) {
      toast.error(error.message || "Error recording catches");
    } finally {
      setLoading(false);
    }
  };

  const isTuna = (species: string) => species.toLowerCase().includes("tuna");

  return (
    <div className="min-h-screen bg-background">
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-card p-8 rounded-lg shadow-2xl animate-scale-in text-center space-y-4">
            <div className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto animate-scale-in">
              <Trophy className="h-10 w-10 text-success-foreground" />
            </div>
            <h2 className="text-2xl font-bold text-gradient">Success!</h2>
            <p className="text-muted-foreground">
              {entries.length} catch{entries.length > 1 ? 'es' : ''} recorded successfully!
            </p>
          </div>
        </div>
      )}
      <Header />
      
      <main className="container py-8 max-w-6xl">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-3xl text-gradient">Bulk Add Catches</CardTitle>
              <Button
                type="button"
                onClick={addEntry}
                variant="outline"
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Another Catch
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">
              {entries.map((entry, index) => (
                <div
                  key={entry.id}
                  className="p-6 border-2 border-border rounded-lg space-y-4 relative"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Catch #{index + 1}</h3>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => duplicateEntry(entry.id)}
                        className="gap-2"
                      >
                        <Copy className="h-4 w-4" />
                        Duplicate
                      </Button>
                      {entries.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeEntry(entry.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor={`species-${entry.id}`}>Fish Species *</Label>
                      <FishSpeciesCombobox
                        id={`species-${entry.id}`}
                        value={entry.fish_species}
                        onValueChange={(value) => updateEntry(entry.id, "fish_species", value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`date-${entry.id}`}>Date Caught *</Label>
                      <Input
                        id={`date-${entry.id}`}
                        type="date"
                        value={entry.date_caught}
                        onChange={(e) => updateEntry(entry.id, "date_caught", e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {isTuna(entry.fish_species) && (
                    <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-lg">
                      <Checkbox
                        id={`tuna-calc-${entry.id}`}
                        checked={entry.useTunaCalculation}
                        onCheckedChange={(checked) =>
                          updateEntry(entry.id, "useTunaCalculation", checked as boolean)
                        }
                      />
                      <Label
                        htmlFor={`tuna-calc-${entry.id}`}
                        className="cursor-pointer font-normal"
                      >
                        Tuna Calculation: Calculate weight using ((Girth × Girth) × Length) / 800
                      </Label>
                    </div>
                  )}

                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor={`weight-${entry.id}`}>Weight (lbs) *</Label>
                      <Input
                        id={`weight-${entry.id}`}
                        type="number"
                        step="0.01"
                        min="0.01"
                        max="10000"
                        value={entry.weight_lbs}
                        onChange={(e) => updateEntry(entry.id, "weight_lbs", e.target.value)}
                        required
                        placeholder="0.00"
                        readOnly={entry.useTunaCalculation && isTuna(entry.fish_species)}
                        className={entry.useTunaCalculation && isTuna(entry.fish_species) ? "bg-muted" : ""}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`length-${entry.id}`}>
                        Length (inches) {entry.useTunaCalculation && isTuna(entry.fish_species) && "*"}
                      </Label>
                      <Input
                        id={`length-${entry.id}`}
                        type="number"
                        step="0.1"
                        min="0"
                        max="500"
                        value={entry.length_inches}
                        onChange={(e) => updateEntry(entry.id, "length_inches", e.target.value)}
                        placeholder="0.0"
                        required={entry.useTunaCalculation && isTuna(entry.fish_species)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`girth-${entry.id}`}>
                        Girth (inches) {entry.useTunaCalculation && isTuna(entry.fish_species) && "*"}
                      </Label>
                      <Input
                        id={`girth-${entry.id}`}
                        type="number"
                        step="0.1"
                        min="0"
                        max="500"
                        value={entry.girth_inches}
                        onChange={(e) => updateEntry(entry.id, "girth_inches", e.target.value)}
                        placeholder="0.0"
                        required={entry.useTunaCalculation && isTuna(entry.fish_species)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`location-${entry.id}`}>Location *</Label>
                    <Input
                      id={`location-${entry.id}`}
                      value={entry.general_location}
                      onChange={(e) => updateEntry(entry.id, "general_location", e.target.value)}
                      required
                      maxLength={200}
                      placeholder="e.g., Gulf of Mexico, Pacific Coast"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label htmlFor={`boat-${entry.id}`}>Boat Name</Label>
                      <Input
                        id={`boat-${entry.id}`}
                        value={entry.boat_name}
                        onChange={(e) => updateEntry(entry.id, "boat_name", e.target.value)}
                        maxLength={100}
                        placeholder="Optional"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`captain-${entry.id}`}>Captain</Label>
                      <Input
                        id={`captain-${entry.id}`}
                        value={entry.captain_name}
                        onChange={(e) => updateEntry(entry.id, "captain_name", e.target.value)}
                        maxLength={100}
                        placeholder="Optional"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`line-${entry.id}`}>Line Test</Label>
                      <Input
                        id={`line-${entry.id}`}
                        value={entry.line_test}
                        onChange={(e) => updateEntry(entry.id, "line_test", e.target.value)}
                        maxLength={50}
                        placeholder="e.g., 30 lb test"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`photo-${entry.id}`}>Photo</Label>
                    <Input
                      id={`photo-${entry.id}`}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        handlePhotoChange(entry.id, file);
                      }}
                    />
                    {entry.photo_file && (
                      <div className="flex items-center gap-4 p-3 border border-border rounded-lg bg-muted/30">
                        <img
                          src={URL.createObjectURL(entry.photo_file)}
                          alt="Preview"
                          className="h-20 w-20 object-cover rounded-md"
                          onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{entry.photo_file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(entry.photo_file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="flex-1 ocean-gradient gap-2">
                  <Save className="h-4 w-4" />
                  {loading ? "Saving..." : `Save ${entries.length} Catch${entries.length > 1 ? "es" : ""}`}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
